import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, FileText, FolderOpen, Users, ClipboardList,
  LogOut, Menu, X, Building2, ChevronDown, Settings, Bell
} from 'lucide-react';

const AppLayout = () => {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
 
  useEffect(() => {
    if (user?.role === 'viewer' && window.location.pathname === '/app') {
      navigate('/app/documents');
    }
  }, [user, navigate]);

  const navItems = user?.role === 'viewer'
    ? [
        { to: '/app/documents', icon: FileText, label: 'Documents' },
      ]
    : [
        { to: '/app', icon: LayoutDashboard, label: 'Dashboard', end: true },
        { to: '/app/documents', icon: FileText, label: 'Documents' },
        { to: '/app/categories', icon: FolderOpen, label: 'Categories' },
        ...(user?.role === 'admin' ? [
          { to: '/app/users', icon: Users, label: 'Users' },
          { to: '/app/audit', icon: ClipboardList, label: 'Audit Logs' },
        ] : []),
      ];

  const roleColors = { admin: 'bg-red-100 text-red-700', editor: 'bg-blue-100 text-blue-700', viewer: 'bg-gray-100 text-gray-600' };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-60' : 'w-14'} bg-white border-r border-gray-200 flex flex-col transition-all duration-200 flex-shrink-0 shadow-sm`}>
        {/* Logo */}
        <div className="flex items-center gap-2.5 p-4 border-b border-gray-100">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-white" />
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900 truncate">DocVault DMS</p>
              <p className="text-xs text-gray-400 truncate">{tenant?.name}</p>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-2 space-y-0.5">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
              }
              title={!sidebarOpen ? label : undefined}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="p-2 border-t border-gray-100">
          <div className={`flex items-center gap-2 p-2 rounded-lg ${sidebarOpen ? '' : 'justify-center'}`}>
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-semibold text-xs flex-shrink-0">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${roleColors[user?.role] || ''}`}>{user?.role}</span>
              </div>
            )}
          </div>
          <button onClick={handleLogout}
            className="sidebar-link sidebar-link-inactive w-full mt-1 text-red-500 hover:text-red-600 hover:bg-red-50"
            title={!sidebarOpen ? 'Sign Out' : undefined}>
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 className="w-4 h-4" />
            <span>{tenant?.name}</span>
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium capitalize ${
              tenant?.plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
              tenant?.plan === 'professional' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>{tenant?.plan}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Welcome, <strong>{user?.name}</strong></span>
          </div>
        </header>

        <div className="p-6 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
